import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, Trash2, Search, X, ExternalLink, ChevronDown, ChevronUp,
  Send, MessageSquare, Calendar, MapPin, DollarSign, User, Mail,
} from 'lucide-react';
import type { JobApplication, JobStatus, JobWorkType, JobSource, JobNote } from '../types';
import {
  fetchJobApplications,
  createJobApplication,
  updateJobApplication,
  deleteJobApplication,
  addJobNote,
} from '../lib/jobApi';

const STATUS_OPTIONS: { value: JobStatus; label: string; color: string }[] = [
  { value: 'saved', label: 'Saved', color: 'bg-gray-500' },
  { value: 'applied', label: 'Applied', color: 'bg-blue-500' },
  { value: 'phone_screen', label: 'Phone Screen', color: 'bg-yellow-500' },
  { value: 'interview', label: 'Interview', color: 'bg-orange-500' },
  { value: 'technical', label: 'Technical', color: 'bg-purple-500' },
  { value: 'offer', label: 'Offer', color: 'bg-green-500' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-500' },
  { value: 'ghosted', label: 'Ghosted', color: 'bg-gray-700' },
];

const SOURCE_OPTIONS: { value: JobSource; label: string }[] = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'helloworld', label: 'HelloWorld' },
  { value: 'jooble', label: 'Jooble' },
  { value: 'indeed', label: 'Indeed' },
  { value: 'glassdoor', label: 'Glassdoor' },
  { value: 'direct_contact', label: 'Direct Contact' },
  { value: 'referral', label: 'Referral' },
  { value: 'company_website', label: 'Company Website' },
  { value: 'other', label: 'Other' },
];

const WORK_TYPE_OPTIONS: { value: JobWorkType; label: string }[] = [
  { value: 'remote', label: 'Remote' },
  { value: 'onsite', label: 'On-site' },
  { value: 'hybrid', label: 'Hybrid' },
];

function getStatusInfo(status: JobStatus) {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
}

function getSourceLabel(source?: JobSource) {
  if (!source) return '';
  return SOURCE_OPTIONS.find((s) => s.value === source)?.label || source;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(dateStr?: string) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ─── Empty form state ───

const EMPTY_FORM = {
  company: '',
  position: '',
  job_url: '',
  status: 'saved' as JobStatus,
  salary_min: '',
  salary_max: '',
  salary_offered: '',
  contact_name: '',
  contact_email: '',
  location: '',
  work_type: 'onsite' as JobWorkType,
  source: '' as JobSource | '',
  cover_letter: '',
  applied_date: '',
  follow_up_date: '',
  interview_date: '',
};

type FormState = typeof EMPTY_FORM;

// ─── Application Form Modal ───

interface FormModalProps {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
  isEdit: boolean;
}

const FormModal: React.FC<FormModalProps> = ({ form, setForm, onSave, onClose, saving, isEdit }) => {
  const set = (field: keyof FormState, value: string) => setForm((p) => ({ ...p, [field]: value }));

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="bg-theme-primary w-full max-w-lg max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-theme-primary">
          <h3 className="text-lg font-semibold text-theme-primary">
            {isEdit ? 'Edit Application' : 'New Application'}
          </h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-theme-secondary">
            <X className="w-5 h-5 text-theme-secondary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Company & Position */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Company *</label>
              <input type="text" value={form.company} onChange={(e) => set('company', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Position *</label>
              <input type="text" value={form.position} onChange={(e) => set('position', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>

          {/* Job URL */}
          <div>
            <label className="text-xs text-theme-secondary block mb-1">Job Posting URL</label>
            <input type="url" value={form.job_url} onChange={(e) => set('job_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>

          {/* Status, Source, Work Type */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500">
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Source</label>
              <select value={form.source} onChange={(e) => set('source', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">--</option>
                {SOURCE_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Work Type</label>
              <select value={form.work_type} onChange={(e) => set('work_type', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500">
                {WORK_TYPE_OPTIONS.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="text-xs text-theme-secondary block mb-1">Location</label>
            <input type="text" value={form.location} onChange={(e) => set('location', e.target.value)}
              placeholder="City, Country"
              className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>

          {/* Salary */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Salary Min</label>
              <input
                type="text"
                inputMode="numeric"
                value={form.salary_min}
                onChange={(e) => set('salary_min', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Salary Max</label>
              <input
                type="text"
                inputMode="numeric"
                value={form.salary_max}
                onChange={(e) => set('salary_max', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Offered</label>
              <input
                type="text"
                inputMode="numeric"
                value={form.salary_offered}
                onChange={(e) => set('salary_offered', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Contact Name</label>
              <input type="text" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Contact Email</label>
              <input type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Applied Date</label>
              <input type="date" value={form.applied_date} onChange={(e) => set('applied_date', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Follow-up</label>
              <input type="date" value={form.follow_up_date} onChange={(e) => set('follow_up_date', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs text-theme-secondary block mb-1">Interview</label>
              <input type="datetime-local" value={form.interview_date} onChange={(e) => set('interview_date', e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
          </div>

          {/* Cover Letter */}
          <div>
            <label className="text-xs text-theme-secondary block mb-1">Cover Letter</label>
            <textarea value={form.cover_letter} onChange={(e) => set('cover_letter', e.target.value)}
              rows={4} placeholder="Paste your cover letter here..."
              className="w-full px-3 py-2 rounded-lg bg-theme-secondary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
          </div>
        </div>

        <div className="p-4 border-t border-theme-primary">
          <button onClick={onSave} disabled={saving || !form.company.trim() || !form.position.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-lg transition-colors text-sm">
            {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───

const JobTracker: React.FC = () => {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<JobSource | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Notes
  const [noteText, setNoteText] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchJobApplications();
      setApplications(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return applications.filter((app) => {
      if (statusFilter !== 'all' && app.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && app.source !== sourceFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!app.company.toLowerCase().includes(q) && !app.position.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [applications, statusFilter, sourceFilter, search]);

  const openNew = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (app: JobApplication) => {
    setEditId(app.id);
    setForm({
      company: app.company,
      position: app.position,
      job_url: app.job_url || '',
      status: app.status,
      salary_min: app.salary_min?.toString() || '',
      salary_max: app.salary_max?.toString() || '',
      salary_offered: app.salary_offered?.toString() || '',
      contact_name: app.contact_name || '',
      contact_email: app.contact_email || '',
      location: app.location || '',
      work_type: app.work_type || 'onsite',
      source: app.source || '',
      cover_letter: app.cover_letter || '',
      applied_date: app.applied_date || '',
      follow_up_date: app.follow_up_date || '',
      interview_date: app.interview_date ? app.interview_date.slice(0, 16) : '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload: any = {
        company: form.company.trim(),
        position: form.position.trim(),
        job_url: form.job_url || null,
        status: form.status,
        salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null,
        salary_offered: form.salary_offered ? Number(form.salary_offered) : null,
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        location: form.location || null,
        work_type: form.work_type,
        source: form.source || null,
        cover_letter: form.cover_letter || null,
        applied_date: form.applied_date || null,
        follow_up_date: form.follow_up_date || null,
        interview_date: form.interview_date ? new Date(form.interview_date).toISOString() : null,
      };

      if (editId) {
        await updateJobApplication(editId, payload);
      } else {
        payload.notes = [];
        await createJobApplication(payload);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await deleteJobApplication(id);
      if (expandedId === id) setExpandedId(null);
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleStatusChange = async (id: string, status: JobStatus) => {
    try {
      await updateJobApplication(id, { status });
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleAddNote = async (id: string) => {
    if (!noteText.trim()) return;
    try {
      await addJobNote(id, noteText.trim());
      setNoteText('');
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-3xl font-bold text-theme-primary mb-2">Application Tracker</h1>
        <p className="text-theme-secondary mb-6">Track your job applications and their status</p>

        {error && (
          <div className="bg-red-500/10 text-red-400 rounded-lg p-3 text-sm mb-4">{error}</div>
        )}

        {/* Add button */}
        <button onClick={openNew}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors mb-4">
          <Plus className="w-5 h-5" /> New Application
        </button>

        {/* Search + Filters */}
        <div className="space-y-2 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-theme-secondary" />
            <input type="text" placeholder="Search company or position..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-theme-primary text-theme-primary text-sm border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)}
              className="px-3 py-1.5 text-xs rounded-lg bg-theme-primary text-theme-primary border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="all">All Statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value as any)}
              className="px-3 py-1.5 text-xs rounded-lg bg-theme-primary text-theme-primary border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="all">All Sources</option>
              {SOURCE_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-center text-theme-secondary py-12">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-theme-secondary py-12">
            {applications.length === 0 ? 'No applications yet. Add one!' : 'No matches for your filters.'}
          </p>
        ) : (
          <div className="space-y-2">
            {filtered.map((app) => {
              const statusInfo = getStatusInfo(app.status);
              const isExpanded = expandedId === app.id;

              return (
                <div key={app.id} className="bg-theme-primary rounded-lg border border-theme-primary overflow-hidden">
                  {/* Summary row */}
                  <div className="flex items-center gap-2 px-4 py-3 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : app.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-theme-primary truncate">{app.company}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full text-white font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <span className="text-xs text-theme-secondary block mt-0.5">{app.position}</span>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {app.source && (
                          <span className="text-[10px] text-theme-secondary bg-theme-secondary px-1.5 py-0.5 rounded">
                            {getSourceLabel(app.source)}
                          </span>
                        )}
                        {app.applied_date && (
                          <span className="text-[10px] text-theme-secondary">{formatDate(app.applied_date)}</span>
                        )}
                        {app.location && (
                          <span className="text-[10px] text-theme-secondary flex items-center gap-0.5">
                            <MapPin className="w-2.5 h-2.5" />{app.location}
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-theme-secondary shrink-0" /> : <ChevronDown className="w-4 h-4 text-theme-secondary shrink-0" />}
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-1 border-t border-theme-primary space-y-3">
                      {/* Quick status change */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-theme-secondary">Status:</span>
                        {STATUS_OPTIONS.map((s) => (
                          <button key={s.value} onClick={() => handleStatusChange(app.id, s.value)}
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors ${
                              app.status === s.value ? `${s.color} text-white` : 'bg-theme-secondary text-theme-secondary hover:text-theme-primary'
                            }`}>
                            {s.label}
                          </button>
                        ))}
                      </div>

                      {/* Details grid */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        {app.job_url && (
                          <div className="col-span-2 flex items-center gap-1">
                            <ExternalLink className="w-3 h-3 text-blue-400" />
                            <a href={app.job_url} target="_blank" rel="noreferrer" className="text-blue-400 hover:underline truncate">
                              {app.job_url}
                            </a>
                          </div>
                        )}
                        {(app.salary_min || app.salary_max) && (
                          <div className="flex items-center gap-1 text-theme-secondary">
                            <DollarSign className="w-3 h-3" />
                            Range: {app.salary_min || '?'} - {app.salary_max || '?'}
                          </div>
                        )}
                        {app.salary_offered && (
                          <div className="flex items-center gap-1 text-green-400">
                            <DollarSign className="w-3 h-3" />
                            Offered: {app.salary_offered}
                          </div>
                        )}
                        {app.contact_name && (
                          <div className="flex items-center gap-1 text-theme-secondary">
                            <User className="w-3 h-3" />{app.contact_name}
                          </div>
                        )}
                        {app.contact_email && (
                          <div className="flex items-center gap-1 text-theme-secondary">
                            <Mail className="w-3 h-3" />{app.contact_email}
                          </div>
                        )}
                        {app.follow_up_date && (
                          <div className="flex items-center gap-1 text-theme-secondary">
                            <Calendar className="w-3 h-3" />Follow-up: {formatDate(app.follow_up_date)}
                          </div>
                        )}
                        {app.interview_date && (
                          <div className="flex items-center gap-1 text-orange-400">
                            <Calendar className="w-3 h-3" />Interview: {formatDateTime(app.interview_date)}
                          </div>
                        )}
                        {app.work_type && (
                          <div className="text-theme-secondary capitalize">{app.work_type}</div>
                        )}
                      </div>

                      {/* Cover letter */}
                      {app.cover_letter && (
                        <details className="text-xs">
                          <summary className="text-theme-secondary cursor-pointer hover:text-theme-primary">Cover Letter</summary>
                          <pre className="mt-1 p-2 rounded bg-theme-secondary text-theme-primary whitespace-pre-wrap text-[11px] max-h-40 overflow-y-auto">
                            {app.cover_letter}
                          </pre>
                        </details>
                      )}

                      {/* Notes timeline */}
                      <div>
                        <h4 className="text-xs font-semibold text-theme-primary mb-2 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> Notes
                        </h4>
                        {(app.notes || []).length > 0 && (
                          <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
                            {[...(app.notes || [])].reverse().map((note: JobNote, i: number) => (
                              <div key={i} className="text-xs bg-theme-secondary rounded p-2">
                                <span className="text-theme-secondary text-[10px]">{formatDateTime(note.date)}</span>
                                <p className="text-theme-primary mt-0.5">{note.text}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-2">
                          <input type="text" value={expandedId === app.id ? noteText : ''}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Add a note..."
                            onKeyDown={(e) => e.key === 'Enter' && handleAddNote(app.id)}
                            className="flex-1 px-3 py-1.5 rounded-lg bg-theme-secondary text-theme-primary text-xs border border-theme-primary focus:outline-none focus:ring-1 focus:ring-blue-500" />
                          <button onClick={() => handleAddNote(app.id)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors">
                            <Send className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => openEdit(app)}
                          className="flex-1 text-xs py-2 rounded-lg bg-theme-secondary text-theme-primary hover:bg-blue-600/20 transition-colors font-medium">
                          Edit
                        </button>
                        <button onClick={() => handleDelete(app.id)}
                          className="px-4 text-xs py-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors font-medium flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Form modal */}
        {showForm && (
          <FormModal form={form} setForm={setForm} onSave={handleSave}
            onClose={() => setShowForm(false)} saving={saving} isEdit={!!editId} />
        )}
      </div>
    </div>
  );
};

export default JobTracker;
